import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import createDocumentClone from "./services/createDocumentClone";
import { deleteWorkspaceById } from "./services/updateWorkspace";
import { RootState } from "@/redux/store";
import { updateFpAppV4Data } from "@/redux/fpAppV4Slice";
import { ShareModal } from "@/components/common/ShareModal";
import { useSharingStatus } from "@/hooks/useSharing";
import { useWorkspaceData } from "../FpEditor/services/useWorkspaceData";
import { useDocumentExport } from "./useDocumentExport";
import {
  ExportIcon,
  ShareNetworkIcon,
  TrashIcon,
  QuestionIcon,
  PencilSimpleLineIcon,
  EyeIcon,
  CaretDownIcon,
  CaretUpIcon,
  CopySimple,
} from "@phosphor-icons/react";

interface WorkspaceControlsProps {
  hideShare?: boolean;
  hideMakeCopy?: boolean;
  hideDelete?: boolean;
  hideModeToggle?: boolean;
  hideExport?: boolean;
}

const WorkspaceControls = ({
  hideShare = false,
  hideMakeCopy = false,
  hideDelete = false,
  hideModeToggle = false,
  hideExport = false,
}: WorkspaceControlsProps = {}) => {
  // The format menu and its options dialog (#13261): the design system's
  // menu, shared with the artifact pane header's Export.
  const documentExport = useDocumentExport();
  const [creatingClone, setCreatingClone] = useState(false);
  // A save in flight: the export hook already follows it.
  const savingDocument = documentExport.disabled;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const workspaceId = useSelector(
    (state: RootState) => state.fpAppV4Data.workspaceId
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const type = useSelector((state: RootState) => state.fpAppV4Data.type) || "";
  const userId = useSelector((state: RootState) => state.userData.userId);
  const { data: workspaceData } = useWorkspaceData(workspaceId || undefined, {
    refetchOnMount: false,
  });
  const { title } = workspaceData || {};
  const isOwner = workspaceData?.user_id === userId;
  const { data: sharingData, isLoading: sharingLoading } = useSharingStatus(
    "workspace",
    workspaceId || null
  );
  const sharingStatusLoaded = !sharingLoading;
  const isViewer = sharingData?.current_user_permission === "view" && !isOwner;
  const isEditable = useSelector(
    (state: RootState) => state.fpAppV4Data.isEditable
  );

  // Force viewing mode when user is a viewer
  const effectiveIsEditable = isViewer ? false : isEditable;

  const [documentMode, setDocumentMode] = useState<"editing" | "viewing">(
    effectiveIsEditable ? "editing" : "viewing"
  );

  useEffect(() => {
    setDocumentMode(effectiveIsEditable ? "editing" : "viewing");
    // Force viewing mode in Redux if viewer
    if (isViewer && isEditable) {
      dispatch(updateFpAppV4Data({ isEditable: false }));
    }
  }, [effectiveIsEditable, isViewer, isEditable, dispatch]);

  const [showModeMenu, setShowModeMenu] = useState(false);
  const [modeMenuAnchor, setModeMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const handleModeChange = (mode: "editing" | "viewing") => {
    if (isViewer && mode === "editing") {
      // Prevent switching to editing mode if viewer
      return;
    }
    setDocumentMode(mode);
    setShowModeMenu(false);
    dispatch(updateFpAppV4Data({ isEditable: mode === "editing" }));
  };

  return (
    <div className="flex items-center h-full w-full">
      <div className="h-full flex items-center justify-end w-full">
        <div className="flex items-center gap-1">
          {/* Export Button */}
          {!hideExport && (
            <Tooltip title="Export">
              <Button
                variant="text"
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  padding: "4px",
                  textTransform: "none",
                  color: savingDocument ? "#888" : "#555",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={(e) => documentExport.open(e.currentTarget)}
                disabled={savingDocument}
              >
                <ExportIcon style={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          )}

          {/* Make Copy Button */}
          {!hideMakeCopy && (
            <Tooltip title="Make Copy">
              <Button
                variant="text"
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  padding: "4px",
                  textTransform: "none",
                  color: savingDocument ? "#888" : "#555",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={async () => {
                  setCreatingClone(true);
                  try {
                    await createDocumentClone(workspaceId, true, false);
                  } catch (error) {
                    (window as Window).openFpToast(
                      "Failed to create duplicate",
                      "error"
                    );
                    console.error("Failed to create duplicate:", error);
                  }
                  setCreatingClone(false);
                }}
                disabled={savingDocument || creatingClone}
              >
                <CopySimple style={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          )}

          {/* Share Button */}
          {!hideShare && (
            <Tooltip title="Share">
              <Button
                variant="text"
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  padding: "4px",
                  textTransform: "none",
                  color: savingDocument ? "#888" : "#555",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => setShowShareModal(true)}
                disabled={savingDocument}
              >
                <ShareNetworkIcon style={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          )}

          {/* Question/Help Button: OUTDATED DOC, commented out for now */}
          {/* <Tooltip title="Help">
            <Button
              variant="text"
              style={{
                minWidth: 0,
                minHeight: 0,
                padding: "4px",
                textTransform: "none",
                color: "#555",
                display: "flex",
                alignItems: "center",
              }}
              onClick={() => {
                window.open(
                  "https://docs.google.com/document/d/1E2qDNS9UeDBCCOMCAknkVqiCysRlx5NWGF_Zt690nqU/edit?usp=sharing",
                  "_blank"
                );
              }}
            >
              <QuestionIcon style={{ fontSize: 16 }} />
            </Button>
          </Tooltip> */}

          {/* Delete Button: only for someone who may manage the document. An
              Editor reached through the agent that wrote it edits but does
              not delete it, and the server refuses them (#13422). */}
          {!hideDelete && sharingData?.can_manage_sharing !== false && (
            <Tooltip title="Move to trash">
              <Button
                variant="text"
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  padding: "4px",
                  textTransform: "none",
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => {
                  const confirm = window.confirm(
                    "Are you sure you want to delete this?"
                  );
                  if (confirm) {
                    deleteWorkspaceById(workspaceId);
                    // Navigate to appropriate page based on workspace type
                    if (type.includes("private_doc_editor")) {
                      navigate("/reports");
                    } else {
                      navigate("/chat");
                    }
                  }
                }}
              >
                <TrashIcon style={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          )}

          {/* Mode Button - Hide when viewer, only show when sharing status is loaded */}
          {!hideModeToggle && sharingStatusLoaded && !isViewer && (
            <>
              <Button
                variant="text"
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  padding: "4px",
                  textTransform: "none",
                  color: savingDocument ? "#888" : "#555",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={(e) => {
                  setShowModeMenu(true);
                  setModeMenuAnchor(e.currentTarget);
                }}
                disabled={savingDocument}
              >
                {documentMode === "editing" ? (
                  <>
                    <PencilSimpleLineIcon style={{ fontSize: 16 }} />
                    <span className="ml-1" style={{ fontSize: "12px" }}>
                      Editing
                    </span>
                  </>
                ) : (
                  <>
                    <EyeIcon style={{ fontSize: 16 }} />
                    <span className="ml-1" style={{ fontSize: "12px" }}>
                      Viewing
                    </span>
                  </>
                )}
                {showModeMenu ? (
                  <CaretUpIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                ) : (
                  <CaretDownIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                )}
              </Button>

              <Menu
                open={showModeMenu}
                onClose={() => setShowModeMenu(false)}
                anchorEl={modeMenuAnchor}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <MenuItem
                  onClick={() => handleModeChange("editing")}
                  selected={documentMode === "editing"}
                  sx={{
                    padding: "0px",
                    fontSize: "14px",
                  }}
                >
                  <div className="text-[12px] px-2 m-1 flex items-center">
                    <PencilSimpleLineIcon
                      style={{ fontSize: 16, marginRight: "4px" }}
                    />
                    <span>Editing</span>
                  </div>
                </MenuItem>
                <MenuItem
                  onClick={() => handleModeChange("viewing")}
                  selected={documentMode === "viewing"}
                  sx={{
                    padding: "0px",
                    fontSize: "14px",
                  }}
                >
                  <div className="text-[12px] px-2 m-1 flex items-center">
                    <EyeIcon style={{ fontSize: 16, marginRight: "4px" }} />
                    <span>Viewing</span>
                  </div>
                </MenuItem>
              </Menu>
            </>
          )}
        </div>
      </div>

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        entityType="workspace"
        entityId={workspaceId || ""}
        entityName={title || "Untitled Document"}
        isOwner={isOwner}
      />
      {documentExport.ui}
    </div>
  );
};

export default WorkspaceControls;
